<script setup lang="ts">
import { computed, watch } from 'vue'
import _ from 'lodash'
import { searchBrands, getBrandDetails } from '@app/api'
import LogoSearchInput from './LogoSearchInput.vue'
import LogoSearchResults from './LogoSearchResults.vue'
import LogoBrandDetails from './LogoBrandDetails.vue'
import LogoPreview from './LogoPreview.vue'
import { useLogoSearchStore } from '../LogoSearchStore'
import { Brand } from './types'

const logoSearchStore = useLogoSearchStore()

// Computed properties to access store state
const searchQuery = computed({
  get: () => logoSearchStore.searchQuery,
  set: (value: string) => logoSearchStore.setSearchQuery(value),
})

// Debounced search function
const performSearch = _.debounce(async () => {
  if (!logoSearchStore.searchQuery.trim()) {
    logoSearchStore.setSearchResults([])
    return
  }

  logoSearchStore.setIsSearching(true)

  try {
    const results = await searchBrands(logoSearchStore.searchQuery.trim())
    logoSearchStore.setSearchResults(results.data)
  } catch (error) {
    console.error('Search failed:', error) // eslint-disable-line no-console
    logoSearchStore.setSearchResults([])
  } finally {
    logoSearchStore.setIsSearching(false)
  }
}, 500)

watch(
  () => logoSearchStore.searchQuery,
  () => {
    performSearch()
  },
)

// Handle brand selection
const selectBrand = async (brand: Brand): Promise<void> => {
  logoSearchStore.setIsLoadingBrand(true)
  try {
    const brandDetails = await getBrandDetails(brand.brand_id)
    const brandWithDetails = {
      ...brand,
      ...brandDetails.data,
    }
    logoSearchStore.setSelectedBrand(brandWithDetails)
  } catch (error) {
    console.error('Failed to load brand details:', error) // eslint-disable-line no-console
  } finally {
    logoSearchStore.setIsLoadingBrand(false)
  }
}
</script>

<template>
  <div class="logo-selector h-100">
    <div class="row h-100">
      <div class="col-md-6 pt-4 border-right">
        <!-- Search Input -->
        <LogoSearchInput v-model="searchQuery" :is-searching="logoSearchStore.isSearching" />

        <!-- Search Results View -->
        <LogoSearchResults
          class="mt-3"
          v-if="logoSearchStore.isInSearchView"
          :search-query="logoSearchStore.searchQuery"
          :search-results="logoSearchStore.searchResults"
          :is-searching="logoSearchStore.isSearching"
          @selectBrand="selectBrand"
        />

        <!-- Brand Details View -->
        <LogoBrandDetails
          v-if="logoSearchStore.isInBrandDetailsView"
          :selected-brand="logoSearchStore.selectedBrand"
          :selected-logo="logoSearchStore.selectedLogo"
          :is-loading-brand="logoSearchStore.isLoadingBrand"
          @goBack="logoSearchStore.goBackToSearch"
          @selectLogo="logoSearchStore.selectLogo"
        />
      </div>

      <!-- Right Column - Preview Area -->
      <div class="col-md-6 py-4">
        <LogoPreview
          :selected-logo="logoSearchStore.selectedLogo"
          :selected-brand="logoSearchStore.selectedBrand"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="sass">
.logo-selector
  // There are multiple child components that use this and we don't want to copy paste this everywhere
  ::v-deep
    span.badge
      padding: 0.3rem 0.5rem
    .badge-outline-secondary
      color: var(--gray-700)
</style>
